import { useCallback, useMemo } from "react";
import {
  type BadgeMapEntry,
  Box,
  createColumnHelper,
  DataTableCellContent,
  type DataTableColumnDef,
  type DataTableGroupType,
  Icon,
  Text,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import {
  FIELD_MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { useLocalGroupPages } from "#app/lib/hooks/use-local-group-pages";
import type { IssueMediaItem } from "#app/hooks/issue-view/use-issue-media-items";
import type { Omittable } from "@salestrak/ui";

// Flattened row: the accessor-backed columns (location, date) read plain
// properties; cells render from the underlying item. Shared by the Media tab
// and the attachments widget.
export interface MediaRow {
  photoId: string;
  index: number;
  source: IssueMediaItem["source"];
  /** Human label for where the photo lives — field name or note author. */
  location: string;
  date: string | null;
  item: IssueMediaItem;
}

// DESIGN-TUNABLE: there is no purple badge tone, so the field source reads as
// `secondary`. Note sources use `info`, which is the soft treatment.
const SOURCE_BADGE: Record<string, BadgeMapEntry> = {
  field: { label: "Field", variant: "secondary" },
  note: { label: "Note", variant: "info" },
  note_inline: { label: "Note (inline)", variant: "info" },
};

const h = createColumnHelper<MediaRow>();

interface UseIssueMediaRowsOptions {
  items: IssueMediaItem[];
}

export function useIssueMediaRows({ items }: UseIssueMediaRowsOptions) {
  const rows = useMemo<MediaRow[]>(
    () =>
      items.map((item, index) => ({
        photoId: item.photoId,
        index,
        source: item.source,
        location:
          item.source === "field"
            ? item.field.name
            : (item.note.author?.name ?? item.note.author?.email ?? "SalesTrak"),
        date: item.source === "field" ? null : item.note.creationDate,
        item,
      })),
    [items],
  );

  const lightboxMedia = useMemo(
    () =>
      rows.map((row) => ({
        id: row.photoId,
        contentType: row.item.contentType,
        fileName: row.item.fileName,
      })),
    [rows],
  );

  return { rows, lightboxMedia };
}

type UseIssueMediaColumnsOptions = Omittable<{
  onOpenLightbox: (index: number) => void;
  /** Quick links — jump to the field row on the summary tab… */
  onNavigateToField?: (customFieldId: string) => void;
  /** …or to the note on the activity tab. */
  onNavigateToNote?: (noteId: string, parentNoteId?: string | null) => void;
  /** Catch-all when a per-source link isn't wired (the attachments widget
   *  routes every jump to the Media tab, which has the full quick links). */
  onNavigateFallback?: () => void;
  tileSize?: number;
}>;

export function useIssueMediaColumns({
  onOpenLightbox,
  onNavigateToField,
  onNavigateToNote,
  onNavigateFallback,
  tileSize = FIELD_MEDIA_TILE_SIZE,
}: UseIssueMediaColumnsOptions): DataTableColumnDef<MediaRow>[] {
  const handleNavigate = useCallback(
    (row: MediaRow) => {
      if (row.item.source === "field" && onNavigateToField) {
        onNavigateToField(row.item.field.customFieldId);
        return;
      }
      if (row.item.source !== "field" && onNavigateToNote) {
        const note = row.item.note;
        onNavigateToNote(note.noteId, note.parentNoteId);
        return;
      }
      onNavigateFallback?.();
    },
    [onNavigateFallback, onNavigateToField, onNavigateToNote],
  );

  return useMemo<DataTableColumnDef<MediaRow>[]>(
    () => [
      h.display({
        id: "photo",
        header: "Photo",
        meta: { minWidth: tileSize + 40, maxWidth: tileSize + 56 },
        cell: ({ row }) => (
          <DataTableCellContent align="start">
            <ResolvedMediaTile
              photoId={row.original.photoId}
              contentType={row.original.item.contentType}
              size={tileSize}
              onClick={() => onOpenLightbox(row.original.index)}
            />
          </DataTableCellContent>
        ),
      }),
      h.hoverAction({
        accessorKey: "location",
        header: "From",
        enableSorting: false,
        meta: { minWidth: 140 },
        showActionButton: true,
        content: (row) =>
          row.item.source === "field" ? (
            <Box direction="row" align="center" gap="xs">
              <Icon
                name={getFieldTypeMeta(row.item.field.type).icon}
                size="xs"
                color="muted"
              />
              <Text size="sm">{row.item.field.name}</Text>
            </Box>
          ) : (
            <EntityTag
              entity={{
                kind: "user",
                id: row.item.note.author?.userId ?? "system",
                user: row.item.note.author ?? null,
              }}
              size="sm"
              system={!row.item.note.author}
              interactive={false}
            />
          ),
        onClickAction: handleNavigate,
        actionLabel: (row) =>
          row.item.source === "field"
            ? `Go to field ${row.item.field.name}`
            : "Go to note",
      }),
      h.badge({
        id: "source",
        header: "Source",
        accessorFn: (row) => row.source,
        badgeMap: SOURCE_BADGE,
        meta: { minWidth: 110, maxWidth: 140 },
      }),
      h.date({
        accessorKey: "date",
        header: "Added",
      }),
    ],
    [handleNavigate, onOpenLightbox, tileSize],
  );
}

export function useIssueMediaGroups(
  rows: MediaRow[],
  /** The owning issue — flips reset the table's scroll window. */
  issueId: string,
): DataTableGroupType<MediaRow>[] {
  const state = useLocalGroupPages(rows, issueId);
  return useMemo(() => {
    const { total, ...groupProps } = state;
    return [{ key: "__all__", label: "Attachments", totalCount: total, ...groupProps }];
  }, [state]);
}
